<template>
    <div v-if="variant === 'resource'" class="code-plain">
        <pre v-html="html" />
    </div>
    <div v-else class="code-block" :class="{ expandable, expanded }">
        <div class="code-wrap">
            <pre ref="code" class="code" v-html="html" />
        </div>
        <button class="code-expand-button" @click="expanded = !expanded">
            <a-glyph name="i-chevon-big-down--16" />
        </button>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onServerPrefetch, ref } from 'vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import type { BodyVariant, CodeBlockToolData } from '@model/editor-js/blocks';

defineOptions({
    name: 'BlockCode',
});

const props = defineProps<{
    id?: string;
    data: CodeBlockToolData;
    variant: BodyVariant;
}>();

/** taller code collapses behind an "expand" button */
const MAX_HEIGHT = 260;

type HighlightFn = (code: string, language: string) => string;

const highlight = ref<HighlightFn | null>(null);
const code = ref<HTMLElement | null>(null);
const expandable = ref(false);
const expanded = ref(false);

// a missing language highlights as plain text
const html = computed(() => highlight.value?.(props.data.code, props.data.language ?? ''));

// highlight.js stays on the server; the client hydrates the highlighted markup
onServerPrefetch(async () => {
    if (!import.meta.env.SSR) return;
    const { highlightCode } = await import('@utils-server/highlight.js');
    highlight.value = highlightCode;
});

onMounted(() => {
    expandable.value = (code.value?.offsetHeight ?? 0) > MAX_HEIGHT;
});
</script>

<style lang="pcss" scoped>
.body-renderer .code-plain pre {
    font-family: ui-monospace, monospace;
    font-size: 14px;
    padding: 10px;
    background-color: var(--av-solid-brand-lightest);
    border: 1px solid var(--av-inversed-primary-darkest);
    border-radius: 10px;
    overflow: auto;
}

.body-renderer .code-block {
    position: relative;
    background-color: var(--av-solid-brand-lightest);
    border: 1px solid rgb(230, 230, 230);
    border-radius: 10px;
    margin-bottom: 16px;

    &.expandable {
        padding-bottom: 32px;

        .code-wrap {
            position: relative;
            max-height: 244px;
            overflow-y: hidden;

            &::after {
                position: absolute;
                bottom: 0;
                inset-inline: 0;
                content: '';
                height: 24px;
                background: linear-gradient(to bottom, transparent, var(--av-solid-brand-lightest));
            }
        }

        .code-expand-button {
            display: block;
            position: absolute;
            padding-bottom: 6px;
            bottom: 0;
            height: 30px;
            inset-inline: 0;
            text-align: center;
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
            cursor: pointer;

            &:hover {
                background-color: var(--av-brand-secondary-accent);
            }
        }
    }

    &.expandable.expanded {
        .code-wrap {
            max-height: none;

            &::after {
                display: none;
            }
        }

        .code-expand-button {
            .a-glyph {
                transform: scaleY(-1);
            }
        }
    }

    .code-expand-button {
        display: none;
    }

    pre {
        @mixin body;
        font-family: ui-monospace, monospace;
        overflow: auto;
        padding: 10px;
        margin: 0;
    }
}
</style>
